import { useEffect, useRef } from 'react';
import { useBranding } from '@/hooks/useBranding';
import { hexToHSLValues, generateChartPalette, adjustForDarkMode } from '@/lib/colorUtils';

const BRANDED_VARS = [
  '--primary', '--primary-hover', '--ring',
  '--sidebar-primary', '--sidebar-ring',
  '--accent', '--support-teal',
  '--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5',
  '--font-family-sans',
] as const;

function applyBranding(primaryHex: string, accentHex: string, isDark: boolean) {
  const root = document.documentElement;
  const p = hexToHSLValues(primaryHex);
  const a = hexToHSLValues(accentHex);
  const palette = generateChartPalette(primaryHex, accentHex);

  const apply = (h: number, s: number, l: number) => {
    if (isDark) {
      const adjusted = adjustForDarkMode(h, s, l);
      return `${adjusted.h} ${adjusted.s}% ${adjusted.l}%`;
    }
    return `${h} ${s}% ${l}%`;
  };

  // From primary color
  root.style.setProperty('--primary', apply(p.h, p.s, p.l));
  root.style.setProperty('--primary-hover', apply(p.h, p.s, Math.max(p.l - 5, 0)));
  root.style.setProperty('--ring', apply(p.h, p.s, p.l));
  root.style.setProperty('--sidebar-primary', apply(p.h, p.s, p.l));
  root.style.setProperty('--sidebar-ring', apply(p.h, p.s, p.l));

  // From accent color
  root.style.setProperty('--accent', apply(a.h, a.s, a.l));
  root.style.setProperty('--support-teal', apply(a.h, a.s, Math.min(a.l + 5, 100)));

  // Chart colors from palette (extract HSL values from hsl() strings)
  for (let i = 0; i < 5; i++) {
    const match = palette[i].match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
      const ch = parseInt(match[1], 10);
      const cs = parseInt(match[2], 10);
      const cl = parseInt(match[3], 10);
      root.style.setProperty(`--chart-${i + 1}`, apply(ch, cs, cl));
    }
  }
}

function clearBranding() {
  const root = document.documentElement;
  for (const v of BRANDED_VARS) {
    root.style.removeProperty(v);
  }
}

/** Load a Google Font by injecting a <link> stylesheet into <head>. */
function loadGoogleFont(fontFamily: string) {
  const linkId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(linkId)) return; // already loaded

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/** Set the browser tab favicon to a custom URL, or reset to default. */
function setFavicon(url: string | null) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url || '/favicon.ico';
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { primaryColor, accentColor, isCustomBranded, fontFamily, orgName, logoUrl } = useBranding();
  const observerRef = useRef<MutationObserver | null>(null);

  // Apply color branding
  useEffect(() => {
    if (!isCustomBranded) {
      clearBranding();
      return;
    }

    const isDark = document.documentElement.classList.contains('dark');
    applyBranding(primaryColor, accentColor, isDark);

    // Observe dark mode class changes
    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const darkNow = document.documentElement.classList.contains('dark');
          applyBranding(primaryColor, accentColor, darkNow);
        }
      }
    });

    observerRef.current.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observerRef.current?.disconnect();
      clearBranding();
    };
  }, [primaryColor, accentColor, isCustomBranded]);

  // Apply custom font
  useEffect(() => {
    const root = document.documentElement;
    if (fontFamily) {
      loadGoogleFont(fontFamily);
      root.style.setProperty('--font-family-sans', `'${fontFamily}'`);
    } else {
      root.style.removeProperty('--font-family-sans');
    }
    return () => {
      root.style.removeProperty('--font-family-sans');
    };
  }, [fontFamily]);

  // Dynamic page title
  useEffect(() => {
    document.title = orgName || 'Vintage';
  }, [orgName]);

  // Dynamic favicon
  useEffect(() => {
    if (logoUrl && logoUrl !== '/logo.png') {
      setFavicon(logoUrl);
    } else {
      setFavicon(null);
    }
    return () => setFavicon(null);
  }, [logoUrl]);

  return <>{children}</>;
}
